import { Injectable, inject } from '@angular/core';
import { GrowthBookService } from '@lentau/core/growth-book/shared/growth-book.service';

const CLIENT_RELOAD_CONFIG_FEATURE_KEY: string = 'web.config.client.reload';

interface ClientReloadSwConfig {
  appInitEnabled: boolean;
  resumeEnabled: boolean;
  minBackgroundMs: number;
}

interface ClientReloadChunkConfig {
  autoReloadEnabled: boolean;
  throttleMs: number;
  maxAttemptsPerSession: number;
}

interface ClientReloadConfig {
  enabled: boolean;
  sw: ClientReloadSwConfig;
  chunk: ClientReloadChunkConfig;
}

const DEFAULT_CLIENT_RELOAD_CONFIG: ClientReloadConfig = {
  enabled: true,
  sw: {
    appInitEnabled: true,
    resumeEnabled: true,
    minBackgroundMs: 180000,
  },
  chunk: {
    autoReloadEnabled: true,
    throttleMs: 60000,
    maxAttemptsPerSession: 2,
  },
};

@Injectable({
  providedIn: 'root',
})
export class ClientReloadPolicyService {
  private readonly growthBookService: GrowthBookService = inject(GrowthBookService);

  public isSwAppInitEnabled(): boolean {
    const config: ClientReloadConfig = this.getConfig();
    return config.enabled && config.sw.appInitEnabled;
  }

  public isSwResumeEnabled(): boolean {
    const config: ClientReloadConfig = this.getConfig();
    return config.enabled && config.sw.resumeEnabled;
  }

  public getSwResumeMinBackgroundMs(): number {
    return this.getConfig().sw.minBackgroundMs;
  }

  public isChunkAutoReloadEnabled(): boolean {
    const config: ClientReloadConfig = this.getConfig();
    return config.enabled && config.chunk.autoReloadEnabled;
  }

  public getChunkReloadThrottleMs(): number {
    return this.getConfig().chunk.throttleMs;
  }

  public getChunkReloadMaxAttemptsPerSession(): number {
    return this.getConfig().chunk.maxAttemptsPerSession;
  }

  private getConfig(): ClientReloadConfig {
    const featureValue: unknown = this.growthBookService.getFeatureValue<Record<string, unknown>>(
      CLIENT_RELOAD_CONFIG_FEATURE_KEY
    ).value;

    if (!this.isRecord(featureValue)) {
      return DEFAULT_CLIENT_RELOAD_CONFIG;
    }

    const swValue: unknown = featureValue.sw;
    const chunkValue: unknown = featureValue.chunk;
    const swRecord: Record<string, unknown> | null = this.isRecord(swValue) ? swValue : null;
    const chunkRecord: Record<string, unknown> | null = this.isRecord(chunkValue) ? chunkValue : null;

    return {
      enabled: this.resolveBoolean(featureValue.enabled, DEFAULT_CLIENT_RELOAD_CONFIG.enabled),
      sw: {
        appInitEnabled: this.resolveBoolean(swRecord?.appInitEnabled, DEFAULT_CLIENT_RELOAD_CONFIG.sw.appInitEnabled),
        resumeEnabled: this.resolveBoolean(swRecord?.resumeEnabled, DEFAULT_CLIENT_RELOAD_CONFIG.sw.resumeEnabled),
        minBackgroundMs: this.resolvePositiveNumber(
          swRecord?.minBackgroundMs,
          DEFAULT_CLIENT_RELOAD_CONFIG.sw.minBackgroundMs
        ),
      },
      chunk: {
        autoReloadEnabled: this.resolveBoolean(
          chunkRecord?.autoReloadEnabled,
          DEFAULT_CLIENT_RELOAD_CONFIG.chunk.autoReloadEnabled
        ),
        throttleMs: this.resolvePositiveNumber(chunkRecord?.throttleMs, DEFAULT_CLIENT_RELOAD_CONFIG.chunk.throttleMs),
        maxAttemptsPerSession: this.resolvePositiveNumber(
          chunkRecord?.maxAttemptsPerSession,
          DEFAULT_CLIENT_RELOAD_CONFIG.chunk.maxAttemptsPerSession
        ),
      },
    };
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private resolveBoolean(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
  }

  private resolvePositiveNumber(value: unknown, fallback: number): number {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;
  }
}
